import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@fluentui/react-components";
import ResponderForm, { 
  ResponderFormData, 
  initialResponderFormData 
} from "#root/pages/Merrge211/ResponderForm";
import { showToast } from "#root/components/GlobalToaster";
import { ROUTES } from "#root/routes/constants";
import { CheckmarkFilled } from "@fluentui/react-icons";
import { useIncidentById } from "#root/queries/incidents";
import { withTenantId, useWrappedQuery } from '#root/hooks/useTenantWrappedQuery';
import { useTenantById } from "#root/queries/tenants";
import { useCreateResponder } from "#root/queries/responders";
import { FILE_KINDS, FILE_REFERENCE_TYPES, useFileUpload } from "#root/hooks/useFileUpload";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from "@fluentui/react-components";

const AddResponderRequestContent: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<ResponderFormData>(initialResponderFormData);
  const { uploadImages } = useFileUpload();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const searchParams = new URLSearchParams(window.location.search);
  const tenantId = searchParams.get('tenantId');
  const incidentId = searchParams.get('incidentId');

  const createResponder = useCreateResponder();

  useEffect(() => {
    if (!tenantId || !incidentId) {
      navigate(ROUTES.ERROR_404);
      return;
    }
  }, [tenantId, incidentId, navigate]);
  
  const { data: incidentResponse, isLoading: incidentLoading } = useWrappedQuery(
    useIncidentById, 
    { id: incidentId! }, 
    tenantId!
  );
  
  const { isLoading: tenantLoading } = useWrappedQuery(
    useTenantById, 
    { id: tenantId! }, 
    tenantId!
  );
  
  const handleFieldChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const verifyRecaptcha = async () => {
    if (!executeRecaptcha) {
      throw new Error('Recaptcha not yet available');
    }

    const token = await executeRecaptcha('responder_registration');
    if (!token) {
      throw new Error('Failed to get reCAPTCHA token');
    }

    return token;
  };

  const handleSubmit = async (submittedData: ResponderFormData) => {
    try {
      setIsSubmitting(true);
      
      await withTenantId(async () => {
        // Get reCaptcha token
        const recaptchaToken = await verifyRecaptcha();
        
        const response = await createResponder.mutateAsync({
          responder: {
            ...submittedData,
            incidentId: incidentId!
          },
          recaptchaToken: recaptchaToken
        });

        const responderId = response.item.id;

        if (submittedData.personalImage || submittedData.licenseImage) {
          const uploadPayload = [];
          
          if (submittedData.personalImage) {
            uploadPayload.push({
              file: submittedData.personalImage,
              referenceId: responderId,
              kind: FILE_KINDS.PERSONAL_IMAGE,
              referenceType: FILE_REFERENCE_TYPES.RESPONDER,
            });
          }
          
          if (submittedData.licenseImage) {
            uploadPayload.push({
              file: submittedData.licenseImage,
              referenceId: responderId, 
              kind: FILE_KINDS.LICENSE_ID,
              referenceType: FILE_REFERENCE_TYPES.RESPONDER,
            });
          }
          
          await uploadImages(uploadPayload);
        }
        
        showToast("Responder registration submitted successfully", "success");
      }, tenantId!);
      
      setIsSubmitted(true);
    } catch (error) {
      showToast("Failed to submit responder registration", "error");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (incidentLoading || tenantLoading) {
    return <Spinner size="large" />;
  }
  
  if (isSubmitted) {
    return (
      <div className="confirmation-container">
        <div className="confirmation">
          <CheckmarkFilled className="success-icon" />
          <div className='subtitle'>Thank you for submitting your Registration.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-responder-page">
      <ResponderForm
        formData={formData}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        incidentName={incidentResponse?.item.name}
        incidentId={incidentResponse?.item.id}
        actions={
          <div className="form-actions">
            <Button 
              appearance="primary"
              size="large"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Register"}
            </Button>
          </div>
        }
      />
    </div>
  );
};

const AddResponderRequest: React.FC = () => {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head'
      }}
    >
      <AddResponderRequestContent />
    </GoogleReCaptchaProvider>
  );
};

export default AddResponderRequest;
